import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTourController } from '../controller';
import { isLiveInputPath } from '../model/liveGuard';
import { OnboardingTooltip } from './OnboardingTooltip';
import { SpotlightLayer } from './SpotlightLayer';
import { useSpotlightTracker } from './useSpotlightTracker';

const READY_DELAY_MS = 100;

/** Bubble of a named guided tour (VOL-141), separate from the "Prise en main" overlay. */
export const TourOverlay = () => {
  const { activeTour, stepIndex, spotlightPosition, next, previous, pause, quit, finish, setSpotlightPosition } =
    useTourController();
  const { t } = useTranslation('onboarding');
  const location = useLocation();
  const navigate = useNavigate();
  const [isReady, setIsReady] = useState(false);

  const step = activeTour?.steps[stepIndex] ?? null;
  const isLive = isLiveInputPath(location.pathname);
  const isVisible = Boolean(step) && !isLive;

  // A step can live on another page: open it first, then the tracker waits for the target.
  // Once per step, so the user stays free to browse elsewhere during a step.
  const routedStepRef = useRef<string | null>(null);
  useEffect(() => {
    if (!step) {
      routedStepRef.current = null;
      return;
    }
    if (routedStepRef.current === step.id) return;
    routedStepRef.current = step.id;
    if (step.route && !location.pathname.startsWith(step.route)) navigate(step.route);
  }, [step, location.pathname, navigate]);

  useEffect(() => {
    if (!isVisible) {
      setIsReady(false);
      return;
    }
    const timer = setTimeout(() => setIsReady(true), READY_DELAY_MS);
    return () => clearTimeout(timer);
  }, [isVisible]);

  const { targetElement, elementFound } = useSpotlightTracker({
    currentStep: step,
    isVisible,
    isReady,
    setSpotlightPosition,
  });

  if (!activeTour || !step || !isVisible || !isReady) return null;

  const total = activeTour.steps.length;
  const title = t(step.titleKey);

  return createPortal(
    <div className="fixed inset-0 z-[10000] pointer-events-none" aria-modal="true" role="dialog" aria-label={title}>
      <SpotlightLayer position={spotlightPosition} targetElement={targetElement} label={title} relayClick={false} />
      <div className="pointer-events-auto">
        <OnboardingTooltip
          step={step}
          position={spotlightPosition}
          onNext={next}
          onPrev={previous}
          onSkip={quit}
          onPause={pause}
          onComplete={() => void finish()}
          isFirst={stepIndex === 0}
          isLast={stepIndex === total - 1}
          isHovered
          currentIndex={stepIndex}
          totalSteps={total}
          elementFound={elementFound}
          requiresNavigation={false}
          isOnRequiredRoute
        />
      </div>
    </div>,
    document.body,
  );
};
